import React from 'react';
import Icon from '../../../components/AppIcon';

const ChannelPerformancePanel = ({ channelData, selectedChannel }) => {
  const filteredData = selectedChannel === 'all' 
    ? channelData 
    : channelData?.filter(c => c?.channel?.toLowerCase()?.includes(selectedChannel));

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Activity" className="w-5 h-5 text-blue-600" />
          Channel Performance Tracking
        </h2>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Channel
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Leads
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Conversions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Conv. Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Cost (PGK)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cost/Lead (PGK)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ROI
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quality
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredData?.map((channel) => (
              <tr key={channel?.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Icon name="Target" className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-900">{channel?.channel}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {channel?.leads}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {channel?.conversions}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900">{channel?.conversionRate}%</span>
                    <div className="ml-2 w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${Math.min(channel?.conversionRate * 2.5, 100)}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  K{channel?.cost?.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  K{channel?.costPerLead?.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-semibold text-green-600">
                    {channel?.roi}%
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    channel?.quality === 'high' ? 'bg-green-100 text-green-800' :
                    channel?.quality === 'medium'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {channel?.quality}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cost-Effectiveness Analysis */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="DollarSign" className="w-5 h-5 text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">Most Cost-Effective</h3>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {filteredData?.reduce((best, channel) => 
              !best || channel?.costPerLead < best?.costPerLead ? channel : best
            , null)?.channel}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            K{filteredData?.reduce((best, channel) => 
              !best || channel?.costPerLead < best?.costPerLead ? channel : best
            , null)?.costPerLead?.toFixed(2)} per lead
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" className="w-5 h-5 text-green-600" />
            <h3 className="text-sm font-medium text-gray-700">Highest ROI</h3>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {filteredData?.reduce((best, channel) => 
              !best || channel?.roi > best?.roi ? channel : best
            , null)?.channel}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {filteredData?.reduce((best, channel) => 
              !best || channel?.roi > best?.roi ? channel : best
            , null)?.roi}% return
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Award" className="w-5 h-5 text-purple-600" />
            <h3 className="text-sm font-medium text-gray-700">Best Conversion</h3>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {filteredData?.reduce((best, channel) => 
              !best || channel?.conversionRate > best?.conversionRate ? channel : best
            , null)?.channel}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {filteredData?.reduce((best, channel) => 
              !best || channel?.conversionRate > best?.conversionRate ? channel : best
            , null)?.conversionRate}% rate
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChannelPerformancePanel;